import { Capacitor } from "@capacitor/core";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { FC } from "react";
import { type AppleAuthOptions, type AppleAuthResponse, appleAuthHelpers, useScript } from "react-apple-signin-auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { AUTH_EVENT, AUTH_METHOD, authEventProps } from "@/pages/signin/authAnalytics";
import { backendService } from "@/services/backend.service";
import { AppPlatform } from "@/utils/models";
import { AppleIcon } from "../icons/AppleIcon";
import { LoginButton } from "./common";

type AppleSSOResult = {
	verifyEmail?: boolean;
	sub?: string;
};

const APPLE_AUTH_OPTIONS = {
	clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
	scope: "email",
	redirectURI: window.location.origin,
	state: `origin:${window.location.origin}`,
	nonce: "nonce",
	usePopup: !Capacitor.isNativePlatform(),
} as const satisfies AppleAuthOptions;

function useAppleSSO(): UseMutationResult<AppleSSOResult, Error, void, unknown> {
	useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

	const { saveTokenData } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<AppleSSOResult, Error, void, unknown>({
		mutationKey: ["useAppleSSO"],
		mutationFn: async () => {
			track("login", { type: "SSO", provider: "apple", status: "start" });

			let appleAuthData: AppleAuthResponse["authorization"];

			if (Capacitor.isNativePlatform()) {
				const stateValue = `origin:${window.location.origin}`;
				const result = await SignInWithApple.authorize({
					clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
					redirectURI: window.location.origin,
					scopes: "email name",
					state: stateValue,
					nonce: "nonce",
				});

				if (!result.response.authorizationCode) {
					throw Error("No authorization code received from Apple");
				}

				if (!result.response.identityToken) {
					throw Error("No identity token received from Apple. Please try signing in again.");
				}

				appleAuthData = {
					id_token: result.response.identityToken,
					code: result.response.authorizationCode,
					state: stateValue,
				};
			} else {
				const appleResponse = await appleAuthHelpers.signIn({
					authOptions: APPLE_AUTH_OPTIONS,
				});

				if (!appleResponse) {
					throw Error("Could not sign in via Apple SSO");
				}

				if (!appleResponse.authorization.code) {
					throw Error("No authorization code received from Apple");
				}

				appleAuthData = appleResponse.authorization;
			}

			const response = await backendService.appleSSO(appleAuthData);
			if (response.status >= 400) {
				throw Error(response.statusText);
			}

			if (response.data.verify_email) {
				track("login", {
					type: "SSO",
					provider: "apple",
					action: "email verification",
				});
				return { verifyEmail: true, sub: response.data.sub };
			}

			const loginResult = await saveTokenData(response.data);
			if (!loginResult.isSuccessful) {
				throw Error("Failed to login");
			}

			track("login", { type: "SSO", provider: "apple", status: "success" });
			track(AUTH_EVENT.LOGIN_SUCCESS, authEventProps(AUTH_METHOD.APPLE, true));

			return { verifyEmail: false };
		},
		onError: (e) => {
			console.error(e);
			trackError("login", {
				type: "SSO",
				provider: "apple",
				status: "fail",
				error: (e as Error).message,
			});
			trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.APPLE, false), reason: (e as Error).message });

			if (e instanceof AxiosError && e.response?.data?.detail) {
				const { responseURL } = e.request || {};

				/** HACK: since `axiosWrapper` also calls `/refresh` on its own then we need to filter messages not related to the login */
				if (typeof responseURL === "string" && !responseURL.endsWith("/token/sso/apple")) {
					showSnackbar(t("error.user.nonexistent.or.inactive"), "error");
				} else {
					const [key, ...params] = e.response.data.detail.split("|");
					const message = params.length > 0 ? t(key, { status: t(`status.${params[0].toLowerCase()}`) }) : t(e.response.data.detail);
					showSnackbar(message, "error");
				}
			} else {
				showSnackbar(e instanceof Error ? e.message : t("error.user.nonexistent.or.inactive"), "error");
			}
		},
	});
}

export const AppleOAuthLoginButton: FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
	const { t } = useTranslation();
	const { mutateAsync: signIn, isPending } = useAppleSSO();
	const navigate = useNavigate();

	if (Capacitor.isNativePlatform() && Capacitor.getPlatform() !== AppPlatform.IOS) {
		return null;
	}

	return (
		<LoginButton
			type="button"
			onClick={async (e) => {
				e.stopPropagation();
				e.preventDefault();
				try {
					const result = await signIn();

					if (result?.verifyEmail && result.sub) {
						navigate("/email-verification", { state: { sub: result.sub } });
					} else {
						navigate("/");
					}
				} catch {
					// Error already handled by useAppleSSO's onError
				}
			}}
			loading={isPending}
			sx={{ ...sx, gap: "1px" }}
		>
			<AppleIcon width={25} height={25} />
			<Box component="span">{t("continue.with.apple")}</Box>
		</LoginButton>
	);
};
